import { useEffect, useState, useMemo } from "react";
import styles from "./TabContents.module.css";
import { supabase } from "../../../../Supabase/SupabaseClient";
import './Estadistica.css';
// Charts
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

/**
 * Estadisticas (mejorada + Chart.js)
 * - Más estilo visual (cards, badges, tablas con striping, hover)
 * - Corregido: "Últimas órdenes" muestra comprador y monto correctamente
 * - Gestor de ingresos ahora incluye gráfico de barras y torta (Chart.js)
 *
 * Dependencias: instala `chart.js` y `react-chartjs-2`
 * npm i chart.js react-chartjs-2
 */

async function safeCount(table) {
  try {
    const res = await supabase.from(table).select("id", { count: "exact", head: true });
    if (res.error) {
      const msg = String(res.error.message || "").toLowerCase();
      if (msg.includes("does not exist") || msg.includes("relation") || msg.includes("no existe")) return null;
      console.warn("safeCount error", table, res.error);
      return null;
    }
    return typeof res.count === "number" ? res.count : (res.count ?? null);
  } catch (err) {
    const em = String(err?.message || "").toLowerCase();
    if (em.includes("does not exist") || em.includes("relation") || em.includes("no existe")) return null;
    console.warn("safeCount caught", table, err);
    return null;
  }
}

async function fetchRecentWithFallback(table, orderCandidates = ["fecha_creacion", "created_at", "fecha", "dia"], limit = 5) {
  for (const col of orderCandidates) {
    try {
      const res = await supabase.from(table).select("*").order(col, { ascending: false }).limit(limit);
      if (res.error) {
        const msg = String(res.error.message || "").toLowerCase();
        if (msg.includes("does not exist") || msg.includes("relation") || msg.includes("no existe")) return { exists: false };
        if (msg.includes("column") && (msg.includes("does not exist") || msg.includes("no existe"))) continue;
        return { exists: true, ok: false, error: res.error };
      }
      return { exists: true, ok: true, data: res.data || [], usedDateColumn: col };
    } catch (err) {
      const em = String(err?.message || "").toLowerCase();
      if (em.includes("does not exist") || em.includes("relation") || em.includes("no existe")) return { exists: false };
      continue;
    }
  }
  // fallback
  try {
    const res = await supabase.from(table).select("*").limit(limit);
    if (res.error) {
      const msg = String(res.error.message || "").toLowerCase();
      if (msg.includes("does not exist") || msg.includes("relation") || msg.includes("no existe")) return { exists: false };
      return { exists: true, ok: false, error: res.error };
    }
    return { exists: true, ok: true, data: res.data || [], usedDateColumn: null };
  } catch (err) {
    return { exists: false };
  }
}

export default function Estadisticas() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [counts, setCounts] = useState({ usuarios: null, ordenes: null, reservas: null, favoritos: null });
  const [recent, setRecent] = useState({ usuarios: [], ordenes: [], reservas: [] });

  // ingresos
  const [fullRevenueMonths, setFullRevenueMonths] = useState([]); // todos los meses calculados
  const [revenueByMonth, setRevenueByMonth] = useState([]); // filtrado por rango
  const [amountField, setAmountField] = useState(null);
  const [revenueRange, setRevenueRange] = useState("12m"); // options: 6m, 12m, all

  const tables = useMemo(() => [
    { key: "usuarios", name: "Usuarios", table: "usuarios" },
    { key: "ordenes", name: "Órdenes", table: "ordenes" },
    { key: "reservas", name: "Reservas", table: "reservas" },
    { key: "favoritos", name: "Favoritos", table: "favoritos_usuario" },
  ], []);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cuando cambia el rango, actualizo el filtrado desde fullRevenueMonths
  useEffect(() => {
    if (!Array.isArray(fullRevenueMonths) || fullRevenueMonths.length === 0) return setRevenueByMonth([]);
    let months = fullRevenueMonths;
    if (revenueRange === '6m') months = months.slice(-6);
    else if (revenueRange === '12m') months = months.slice(-12);
    setRevenueByMonth(months);
  }, [revenueRange, fullRevenueMonths]);

  const refreshAll = async () => {
    setLoading(true);
    setError(null);
    try {
      // counts
      const countsP = tables.map(t => safeCount(t.table).then(c => ({ key: t.key, count: c })));
      // recientes
      const recentUsuariosP = fetchRecentWithFallback("usuarios");
      const recentOrdenesP = fetchRecentWithFallback("ordenes", [ "fecha_creacion", "fecha"]);
      const recentReservasP = fetchRecentWithFallback("reservas", ["dia", "fecha", "created_at"]);
      // además obtenemos órdenes (hasta 5000) para calcular ingresos
      const allOrdersP = supabase.from("ordenes").select("*").limit(5000);

      const [countsRes, usuariosRes, ordenesRes, reservasRes, allOrdersRes] = await Promise.all([
        Promise.all(countsP), recentUsuariosP, recentOrdenesP, recentReservasP, allOrdersP
      ]);

      // set counts
      const newCounts = { ...counts };
      for (const r of countsRes) newCounts[r.key] = (typeof r.count === "number") ? r.count : null;
      setCounts(newCounts);

      // set recents (ordenes: attach buyer info if possible)
      const recentsObj = {
        usuarios: usuariosRes.exists && usuariosRes.ok ? (usuariosRes.data || []) : [],
        ordenes: ordenesRes.exists && ordenesRes.ok ? (ordenesRes.data || []) : [],
        reservas: reservasRes.exists && reservasRes.ok ? (reservasRes.data || []) : [],
      };

      // intentar resolver comprador para las órdenes recientes
      if (Array.isArray(recentsObj.ordenes) && recentsObj.ordenes.length > 0) {
        // detectar campo id usuario
        const userIdFields = ["usuario_id", "user_id", "cliente_id", "idCliente", "clienteid"];
        const ids = new Set();
        recentsObj.ordenes.forEach(o => {
          // si el objeto ya trae un usuario embebido
          if (o.usuario && (o.usuario.id || o.usuario.nombre)) {
            o._buyer = { nombre: o.usuario.nombre, email: o.usuario.email };
            return;
          }
          for (const f of userIdFields) if (Object.prototype.hasOwnProperty.call(o, f) && o[f]) ids.add(String(o[f]));
        });

        if (ids.size > 0) {
          // buscar usuarios
          const idList = Array.from(ids);
          try {
            const usersRes = await supabase.from("usuarios").select("id, nombre, email").in("id", idList);
            const usersById = {};
            if (!usersRes.error && Array.isArray(usersRes.data)) usersRes.data.forEach(u => usersById[String(u.id)] = u);
            // attach
            recentsObj.ordenes = recentsObj.ordenes.map(o => {
              if (o._buyer) return o; // ya asignado
              let buyer = null;
              for (const f of userIdFields) if (Object.prototype.hasOwnProperty.call(o, f) && o[f]) { buyer = usersById[String(o[f])] || null; break; }
              // fallback: maybe the order already has nombre/email fields
              if (!buyer) buyer = (o.nombre || o.cliente || o.email) ? { nombre: o.nombre || o.cliente || o.email, email: o.email } : null;
              return { ...o, _buyer: buyer };
            });
          } catch (err) {
            console.warn("Error fetching users for orders:", err);
            recentsObj.ordenes = recentsObj.ordenes.map(o => ({ ...o, _buyer: o.nombre || o.email ? { nombre: o.nombre || o.email, email: o.email } : null }));
          }
        } else {
          // no user ids found -> try to attach name/email from order fields
          recentsObj.ordenes = recentsObj.ordenes.map(o => ({ ...o, _buyer: o.nombre || o.email ? { nombre: o.nombre || o.email, email: o.email } : null }));
        }
      }

      setRecent(recentsObj);

      // PROCESAR ingresos: usar allOrdersRes
      if (allOrdersRes && !allOrdersRes.error && Array.isArray(allOrdersRes.data)) {
        const ordersAll = allOrdersRes.data;
        // detectar campo de monto
        const amountCandidates = ["montoTotal", "monto", "total", "amount", "precio", "precio_unitario", "price", "valor"];
        let detected = null;
        if (ordersAll.length > 0) {
          const sample = ordersAll[0];
          for (const c of amountCandidates) if (Object.prototype.hasOwnProperty.call(sample, c)) { detected = c; break; }
        }
        setAmountField(detected);

        // agrupar por mes
        const map = {};
        for (const o of ordersAll) {
          const raw = o.created_at ?? o.fecha_creacion ?? o.fecha ?? o.day ?? o.dia;
          if (!raw) continue;
          const d = new Date(raw);
          if (isNaN(d.getTime())) continue;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          const amtRaw = detected ? (o[detected] ?? 0) : (o.total ?? o.monto ?? 0);
          const amt = parseFloat(String(amtRaw ?? 0).toString().replace(/,/g, '.').replace(/[^0-9.\--]/g, '')) || 0;
          map[key] = map[key] || { total: 0, sales: 0 };
          map[key].total += amt;
          map[key].sales += 1;
        }
        const months = Object.entries(map)
          .map(([month, v]) => ({ month, total: v.total, sales: v.sales }))
          .sort((a, b) => a.month.localeCompare(b.month));

        setFullRevenueMonths(months);
        // filtered handled by useEffect
      } else {
        setFullRevenueMonths([]);
        setAmountField(null);
        setRevenueByMonth([]);
      }

    } catch (err) {
      console.error("Error al refrescar estadísticas:", err);
      setError("Error al cargar estadísticas. Revisa la consola para más detalles.");
    } finally {
      setLoading(false);
    }
  };

  const totalAvailableCounts = Object.values(counts).filter(v => typeof v === 'number');
  const maxCount = totalAvailableCounts.length ? Math.max(...totalAvailableCounts) : 1;

  const formatNumber = (n) => {
    if (n === null || typeof n !== 'number') return '—';
    return n.toLocaleString();
  };

  const displayOrderAmount = (o) => {
    const candidates = [amountField, 'total', 'montoTotal', 'monto', 'price', 'precio', 'precio_unitario', 'amount'];
    for (const c of candidates) {
      if (!c) continue;
      if (Object.prototype.hasOwnProperty.call(o, c) && o[c] != null) {
        const v = parseFloat(String(o[c]).toString().replace(/,/g, '.').replace(/[^0-9.\--]/g, ''));
        return isNaN(v) ? null : v.toLocaleString();
      }
    }
    return null;
  };

  // --- Chart data derived from revenueByMonth ---
  const chartLabels = revenueByMonth.map(r => r.month);
  const chartTotals = revenueByMonth.map(r => Number(r.total || 0));
  const chartSales = revenueByMonth.map(r => Number(r.sales || 0));

  const barData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Monto',
        data: chartTotals,
        backgroundColor: 'rgba(59,130,246,0.8)',
        borderRadius: 6,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: '#f3f4f6' } },
    },
  };

  const pieData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Ventas',
        data: chartSales,
        backgroundColor: chartLabels.map((_, i) => `hsl(${(i * 45) % 360} 80% 60%)`),
      },
    ],
  };

  const pieOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };

  return (
    <div className={styles.tabContent}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <h2 className={styles.tabTitle}>Estadísticas</h2>
        <div>
          <button onClick={refreshAll} className={styles.btnPrimary} disabled={loading} style={{ marginRight: 8 }}>
            {loading ? 'Actualizando...' : 'Refrescar'}
          </button>
        </div>
      </div>

      {error && <div className={styles.error} style={{ marginBottom: 12 }}>{error}</div>}

      <p style={{ marginTop: 0, marginBottom: 16 }}>Visión general rápida de las tablas clave: usuarios, órdenes, reservas y favoritos.</p>

      {/* Totales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 18 }}>
        {tables.map(t => (
          <div key={t.key} className={styles.card ?? undefined} style={{ padding: 14, borderRadius: 12, boxShadow: '0 6px 18px rgba(15,23,42,0.04)', background: '#fff', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{t.name}</div>
              <div style={{ fontSize: 12, color: '#93c5fd', background: '#eef8ff', padding: '6px 8px', borderRadius: 999 }}>{counts[t.key] === null ? '—' : (typeof counts[t.key] === 'number' ? counts[t.key] : '—')}</div>
            </div>
            <div style={{ marginTop: 12, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{formatNumber(counts[t.key])}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>{counts[t.key] === null ? 'No disponible / tabla inexistente' : 'Total registrado'}</div>
          </div>
        ))}
      </div>

      {/* Comparativa */}
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 8px 0' }}>Comparativa</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', minHeight: 140 }}>
          {tables.map(t => {
            const value = typeof counts[t.key] === 'number' ? counts[t.key] : 0;
            const heightPercent = maxCount > 0 ? Math.round((value / maxCount) * 100) : 0;
            return (
              <div key={t.key} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 140, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                  <div style={{ width: '70%', height: `${heightPercent}%`, background: '#3b82f6', borderRadius: 8, transition: 'height 300ms ease', boxShadow: 'inset 0 -6px 12px rgba(59,130,246,0.12)' }} title={`${t.name}: ${formatNumber(value)}`} />
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: '#111827' }}>{t.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gestor de ingresos con gráficos */}
      <div style={{ marginBottom: 18, padding: 12, borderRadius: 10, background: '#fff', boxShadow: '0 6px 18px rgba(2,6,23,0.04)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Gestor de ingresos</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#6b7280' }}>Rango:</label>
            <select value={revenueRange} onChange={e => setRevenueRange(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e6eefc' }}>
              <option value="6m">Últimos 6 meses</option>
              <option value="12m">Últimos 12 meses</option>
              <option value="all">Todo</option>
            </select>
            <button onClick={refreshAll} className={styles.btnSecondary} style={{ marginLeft: 8 }}>Actualizar</button>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <small style={{ color: '#6b7280' }}>Campo detectado para monto: <strong>{amountField ?? 'no detectado'}</strong></small>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
  <div className="chartWrapper">
    <h4 style={{ margin: '0 0 8px 0' }}>Ingresos (monto) — barras</h4>
    <div style={{ height: '100%' }}>
      <Bar data={barData} options={barOptions} />
    </div>
  </div>

  <div className="chartWrapper">
    <h4 style={{ margin: '0 0 8px 0' }}>Distribución de ventas — torta</h4>
    <div style={{ height: '100%' }}>
      <Pie data={pieData} options={pieOptions} />
    </div>
  </div>
</div>


        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ padding: 12, borderRadius: 8, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Total ventas (periodo)</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{revenueByMonth.reduce((a, b) => a + (b.sales || 0), 0)}</div>
          </div>
          <div style={{ padding: 12, borderRadius: 8, background: '#f8fafc' }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Total monto (periodo)</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{Number(revenueByMonth.reduce((a, b) => a + (b.total || 0), 0)).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Listas recientes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: 12, borderRadius: 8, background: '#fff', boxShadow: '0 6px 18px rgba(2,6,23,0.02)' }}>
          <h4 style={{ marginTop: 0 }}>Últimos usuarios</h4>
          {recent.usuarios.length === 0 ? <p style={{ color: '#6b7280' }}>No hay usuarios recientes.</p> :
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {recent.usuarios.map(u => (
                <li key={u.id ?? JSON.stringify(u)} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 999, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#1e40af' }}>{(u.nombre || u.email || 'U').toString().charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{u.nombre ?? u.email ?? 'Sin nombre'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{u.email ?? u.telefono ?? ''}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          }
        </div>

        <div style={{ padding: 12, borderRadius: 8, background: '#fff', boxShadow: '0 6px 18px rgba(2,6,23,0.02)' }}>
          <h4 style={{ marginTop: 0 }}>Últimas órdenes</h4>
          {recent.ordenes.length === 0 ? <p style={{ color: '#6b7280' }}>No hay órdenes recientes.</p> :
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {recent.ordenes.map(o => (
                <li key={o.id ?? JSON.stringify(o)} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{o.descripcion ?? o.titulo ?? `Orden ${o.id ?? ''}`}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                        {o._buyer ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 28, height: 28, borderRadius: 999, background: '#fff7ed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#b45309' }}>{(o._buyer.nombre || o._buyer.email || '').toString().charAt(0).toUpperCase()}</span>
                            <span>{o._buyer.nombre ?? o._buyer.email}</span>
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>Comprador no disponible</span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700 }}>{displayOrderAmount(o) ? `$ ${displayOrderAmount(o)}` : '—'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{o.estado ?? (o.created_at ?? o.fecha_creacion ?? '')}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          }
        </div>

        <div style={{ padding: 12, borderRadius: 8, background: '#fff', boxShadow: '0 6px 18px rgba(2,6,23,0.02)' }}>
          <h4 style={{ marginTop: 0 }}>Últimas reservas</h4>
          {recent.reservas.length === 0 ? <p style={{ color: '#6b7280' }}>No hay reservas recientes.</p> :
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {recent.reservas.map(r => (
                <li key={r.id ?? JSON.stringify(r)} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontWeight: 700 }}>{r.nombre ?? r.servicio ?? `Reserva ${r.id ?? ''}`}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{r.dia ?? r.fecha ?? r.horario ?? ''}</div>
                </li>
              ))}
            </ul>
          }
        </div>

      </div>
    </div>
  );
}
//Falta corregir la responsividad de los graficos